import braintreeDropin from 'braintree-web-drop-in';
import { loadStripe } from '@stripe/stripe-js';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { change, clearSubmitErrors, Form, formValueSelector, reduxForm } from 'redux-form';

import { BaseForm } from 'components/Form';
import Loading from 'components/Loading';
import MessageBox from 'components/MessageBox/index';
import Retry from 'components/Retry';
import { fetchById as fetchFacility } from 'entities/Facility/actions';
import { fetchToken as fetchClientToken } from 'entities/Payment/actions';
import './styles.scss';

/* Pay form. */
class PayForm extends BaseForm {
  constructor(props) {
    // parent, for lifecycle logging
    super(props);

    // keep track of facility loading
    this.state = {
      ...this.state,
      fetchError: false,
      loading: false,
      facility: undefined,
      clientToken: null,

      // drop-in state
      braintreeInstance: null,
      stripeElements: null,
      stripePaymentElement: null,
      stripeInstance: null,
      processorError: null,
      widgetMounted: false,
      widgetComplete: false,
      widgetKey: 0,
    };
  }

  componentDidMount() {
    // parent, for lifecycle logging
    super.componentDidMount();

    if (!this.state.loading) {
      this.loadFacility(this.props);
    }
  }

  loadFacility(props) {
    if (props.facilityId) {
      // if we already have the desired facility, do nothing
      if (this.state.facility && String(this.state.facility.id) === String(props.facilityId)) {
        return;
      }

      // loading...
      this.setState({
        fetchError: false,
        loading: true,
        facility: null,
      });

      // fetch it
      props
        .fetchById(props.facilityId)
        .then((facility) => {
          // set it on the state
          this.setState({
            fetchError: false,
            facility: facility,
          });

          // initialize payments once facility is loaded
          this.initializePayments(facility);
        })
        .catch((e) => {
          // log it
          console.error('Error fetching facility:', e);

          // flag it so we can show an error
          this.setState({ fetchError: true });
        })
        .finally(() => {
          // no longer loading
          this.setState({
            loading: false,
          });
        });
    } else if (this.state.facility) {
      // clear the facility
      this.setState({
        fetchError: false,
        loading: false,
        facility: undefined,
      });
    }
  }

  initializePayments(facility) {
    // we require HTTPS
    const isSecure = window.location.protocol === 'https:';
    if (!isSecure) {
      console.error('Payment widgets require a secure connection (HTTPS).');
      this.setState({
        processorError: 'A secure connection (HTTPS) is required to process payments.',
      });
      return;
    }

    // get the configured processor
    const digitalProcessor = facility?.digitalPaymentProcessor;
    let isBraintree = digitalProcessor === 'BTD';
    let isStripe = digitalProcessor === 'STD';

    // we need the provider's token
    this.props.loadClientToken(this.props.facilityId).then((token) => {
      if (!token) {
        console.error('Error fetching payment token');
        this.setState({
          processorError:
            'We are currently unable to accept payments. Please go back and select a postpaid rate or try again later.',
        });
        return;
      }
      this.setState({ clientToken: token });

      // pull in the correct drop-in based on the provider
      if (isBraintree) {
        braintreeDropin.create(
          {
            authorization: token,
            container: `#braintree-dropin-container-${this.state.widgetKey}`,
            paypal: this.props.status?.payments?.payPal
              ? { flow: 'checkout', amount: this.props.amount, currency: 'USD' }
              : undefined,
            applePay: this.props.status?.payments?.applePay
              ? {
                  displayName: facility?.braintree?.brandName || `Park 'N Go`,
                  paymentRequest: {
                    total: {
                      type: 'final',
                      label: facility?.braintree?.brandName || `Park 'N Go`,
                      amount: this.props.amount,
                    },
                  },
                }
              : undefined,
            googlePay:
              this.props.status?.payments?.googlePay && facility?.braintree?.googleMerchantId
                ? {
                    googlePayVersion: 2,
                    merchantId: facility.braintree.googleMerchantId,
                    transactionInfo: {
                      totalPriceStatus: 'FINAL',
                      totalPrice: String(this.props.amount),
                      currencyCode: 'USD',
                    },
                  }
                : undefined,
          },
          (createErr, instance) => {
            // did it fail?
            if (createErr) {
              console.error('Error creating Braintree drop-in', createErr);
              this.setState({
                processorError:
                  'We are currently unable to accept payments. Please go back and select a postpaid rate or try again later.',
              });
              return;
            }

            // wire up drop-in events
            instance.on('paymentMethodRequestable', () => {
              this.setState({ widgetComplete: true });
            });
            instance.on('noPaymentMethodRequestable', () => {
              this.setState({ widgetComplete: false });
            });

            // set the instance and flag as mounted
            this.setState({ braintreeInstance: instance, widgetMounted: true });
          },
        );
      } else if (isStripe) {
        loadStripe(token)
          .then((stripe) => {
            // did it fail?
            if (!stripe) {
              this.setState({
                processorError:
                  'We are currently unable to accept payments. Please go back and select a postpaid rate or try again later.',
              });
              return;
            }

            // create elements
            const elements = stripe.elements({
              mode: 'payment',
              amount: Math.max(Math.round(this.props.amount * 100), 50),
              currency: 'usd',
              paymentMethodCreation: 'manual',
              theme: 'flat',
            });
            const paymentElement = elements.create('payment');

            // wire up events
            paymentElement.on('change', (event) => {
              this.setState({ widgetComplete: event.complete });
            });

            // mount
            paymentElement.mount(`#stripe-payment-element-${this.state.widgetKey}`);

            // set the instance and flag as mounted
            this.setState({
              stripeInstance: stripe,
              stripeElements: elements,
              stripePaymentElement: paymentElement,
              widgetMounted: true,
            });
          })
          .catch((err) => {
            console.error('Stripe load error', err);
            this.setState({
              processorError:
                'We are currently unable to accept payments. Please go back and select a postpaid rate or try again later.',
            });
          });
      }
    });
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    // tear down the payment processor instances
    if (this.state.braintreeInstance) {
      this.state.braintreeInstance.teardown();
    }
    if (this.state.stripePaymentElement) {
      this.state.stripePaymentElement.destroy();
    }
  }

  reloadWidget() {
    // tear down the payment processor instances
    if (this.state.braintreeInstance) {
      try {
        this.state.braintreeInstance.teardown().catch((e) => console.error(e));
      } catch (e) {
        console.error('Error tearing down Braintree', e);
      }
    }
    if (this.state.stripePaymentElement) {
      try {
        this.state.stripePaymentElement.destroy();
      } catch (e) {
        console.error('Error destroying Stripe element', e);
      }
    }

    // reset the state
    this.setState((prevState) => ({
      widgetKey: (prevState.widgetKey || 0) + 1,
      braintreeInstance: null,
      stripeElements: null,
      stripePaymentElement: null,
      stripeInstance: null,
      processorError: null,
      widgetMounted: false,
      widgetComplete: false,
      clientToken: null,
    }));
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    // load facility if not already loaded
    if (!this.state.loading && this.state.facility === undefined) {
      this.loadFacility(this.props);
    }

    // update stripe amount if changed
    if (this.state.stripeElements && prevProps.amount !== this.props.amount) {
      this.state.stripeElements.update({
        amount: Math.max(Math.round(this.props.amount * 100), 50),
      });
    }

    // if the nonce was cleared in a manner other than a full-form reset,
    // we need to re-render the payment widget
    if (prevProps.nonce && !this.props.nonce && !this.props.pristine) {
      this.reloadWidget();
    }

    // if widgetKey has changed, we need to re-init the drop-in
    if (prevState.widgetKey !== this.state.widgetKey && this.state.facility) {
      this.initializePayments(this.state.facility);
    }
  }

  handlePaymentSubmit = async (values) => {
    // if we have a nonce, submit it
    if (this.props.nonce) {
      this.props.onSubmit(values);
      return;
    }

    // process payment
    if (this.state.braintreeInstance) {
      this.state.braintreeInstance.requestPaymentMethod((err, payload) => {
        if (err) {
          console.error('Braintree error on submission', err);
          return;
        }

        // capture the nonce and submit
        this.props.dispatch(change(this.props.form, 'payment.nonce', payload.nonce));
        this.props.onSubmit({ ...values, payment: { ...values?.payment, nonce: payload.nonce } });
      });
    } else if (this.state.stripeInstance && this.state.stripeElements) {
      // first, validate the payment element
      const { error: submitError } = await this.state.stripeElements.submit();
      if (submitError) {
        console.error('Stripe validation error', submitError);
        return;
      }

      // create payment method
      const { error, paymentMethod } = await this.state.stripeInstance.createPaymentMethod({
        elements: this.state.stripeElements,
      });
      if (error) {
        console.error('Stripe error on submission', error);
        return;
      }

      // capture the payment nonce and submit
      this.props.dispatch(change(this.props.form, 'payment.nonce', paymentMethod.id));
      this.props.onSubmit({ ...values, payment: { ...values?.payment, nonce: paymentMethod.id } });
    } else {
      // shouldn't happen
      this.props.onSubmit(values);
    }
  };

  render() {
    super.render();

    // do we have an error?
    if (this.state.fetchError) {
      return <Retry onRefresh={() => this.loadFacility(this.props)} />;
    } else if (!this.state.facility && this.state.loading) {
      // still loading...
      return (
        <div className="row">
          <div className="col-12 text-center png-content-loading">
            <Loading />
          </div>
        </div>
      );
    }

    // determine payment processor
    const digitalProcessor = this.state.facility?.digitalPaymentProcessor;
    let isBraintree = digitalProcessor === 'BTD';
    let isStripe = digitalProcessor === 'STD';

    return (
      <Form
        id={this.props.form}
        onSubmit={this.props.handleSubmit(this.handlePaymentSubmit)}
        className="png-reservation-book-form"
      >
        {/* do we have a top-level error? */}
        {this.props.error && (
          <div className="has-error">
            <div className="png-form-error">{this.props.error}</div>
          </div>
        )}

        {/* do we have a processor error? */}
        {this.state.processorError ? (
          <MessageBox flavor="danger">{this.state.processorError}</MessageBox>
        ) : (
          <>
            {/* render the drop-in */}
            <div className="card">
              <div className="card-header png-reservation-book-payment-type">
                <div className="d-flex justify-content-between align-items-center">
                  <span>Pay ${this.props.amount.toFixed(2)}</span>
                </div>
              </div>
              <div className="card-body">
                <div
                  key={`bt-${this.state.widgetKey}`}
                  id={`braintree-dropin-container-${this.state.widgetKey}`}
                  className={isBraintree ? '' : 'd-none'}
                ></div>
                <div
                  key={`st-${this.state.widgetKey}`}
                  id={`stripe-payment-element-${this.state.widgetKey}`}
                  className={isStripe ? '' : 'd-none'}
                ></div>

                {!this.state.widgetMounted && (
                  <div className="text-center p-3">
                    <Loading />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* if this is an update to a prepaid with an amount change, show a message */}
        {this.props.original &&
        this.props.original.prepaid &&
        this.props.original.prepaid !== this.props.amount ? (
          <div className="row">
            <div className="col mt-3">
              <MessageBox flavor="info">
                You will be refunded ${this.props.original.prepaid.toFixed(2)} for the original
                reservation before being charged ${this.props.amount.toFixed(2)} for the new
                reservation.
              </MessageBox>
            </div>
          </div>
        ) : (
          <></>
        )}

        <div className="form-row png-reservation-buttons mt-4">
          <div className="form-group png-reservation-button col-5">
            <button
              type="button"
              onClick={() => this.props.onPrevious()}
              className="btn btn-primary btn-lg png-reservation-book-previous"
            >
              Back
            </button>
          </div>

          <div className="form-group col-2"></div>

          <div className="form-group png-reservation-button col-5">
            <button
              type="submit"
              className="btn btn-primary btn-lg png-reservation-book-next"
              disabled={
                this.props.submitting ||
                this.state.processorError ||
                !this.state.widgetMounted ||
                !this.state.widgetComplete
              }
            >
              {this.props.original ? 'Update' : 'Book'}
            </button>
          </div>
        </div>
      </Form>
    );
  }
}

// decorate with reduxForm()
PayForm = reduxForm({
  // preserve form data throughout the flow
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,

  // clear form-level errors on change
  onChange: (_, dispatch, props) => {
    if (props.error) {
      dispatch(clearSubmitErrors(props.form));
    }
  },
})(PayForm);

// map state to properties relevant to this component
const mapStateToProps = (state, ownProps) => ({
  // status
  status: state.context.status,

  // amount owed
  amount: formValueSelector(ownProps.form)(state, 'payment.amount')
    ? formValueSelector(ownProps.form)(state, 'payment.amount')
    : 0,

  // digital nonce
  nonce: formValueSelector(ownProps.form)(state, 'payment.nonce'),

  // test mode?
  testMode: state.testMode.enabled || localStorage.getItem('testMode'),
});

// map dispatch function to callback props so that the component can invoke them
const mapDispatchToProps = (dispatch, ownProps) => ({
  // fetch a single facility
  fetchById: (facilityId, test) => {
    return dispatch(fetchFacility(facilityId, test));
  },

  // loads the client payment token
  loadClientToken: (facilityId) => {
    return dispatch(fetchClientToken(facilityId)).catch((e) => {
      console.error('Error loading client payment token', e);
      return null;
    });
  },
});

// turn this into a container component
PayForm = withRouter(connect(mapStateToProps, mapDispatchToProps)(PayForm));

// set default props
PayForm.defaultProps = {
  form: 'reservationForm',
};

export default PayForm;
